// Backend status choices
export type RiskStatus = 'DRAFT' | 'ACTIVE' | 'CLOSED' | 'ARCHIVED';
// Backend treatment plan choices
export type TreatmentPlan = 'MITIGATE' | 'TRANSFER' | 'AVOID' | 'ACCEPT';
// Frontend display enums
export type RiskLikelihood = 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH';
export type RiskImpact = 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH';
// RiskItem.category is a free-text CharField with no choices on the backend,
// so this is an alias, not a closed set. Real registers carry values like
// Cybersecurity and Third-Party / Vendor that no fixed union would admit.
export type RiskCategory = string;

export interface Risk {
  id: string;
  title: string;
  description: string;
  reference_number: string;
  category: string;  // Backend uses string, not enum
  status: RiskStatus;
  
  // Organization fields (exactly one should be set)
  organizationLevel?: string;
  company?: string;
  company_name?: string;
  team?: string;
  team_name?: string;
  department?: string;
  department_name?: string;
  
  // Risk ownership
  owner: string;
  owner_email?: string;
  owner_full_name?: string;

  // Free-text vendor/supplier note (vendor management lives elsewhere)
  vendor_note?: string;

  // Inherent risk values (before treatment) - backend uses integers 1-5
  inherent_likelihood: number;
  inherent_impact: number;
  inherent_notes?: string;
  inherent_score?: number;
  inherent_risk_level?: string;
  
  // Treatment
  treatment_plan: TreatmentPlan;
  treatment_details?: string;
  treatment_owner?: string;
  treatment_owner_email?: string;
  treatment_owner_full_name?: string;
  treatment_due_date?: string;
  
  // Residual risk values (after treatment) - backend uses integers 1-5
  residual_likelihood: number;
  residual_impact: number;
  residual_notes?: string;
  residual_score?: number;
  residual_risk_level?: string;
  risk_reduction?: number;
  
  // Status fields
  is_complete: boolean;
  completed_at?: string;
  
  // Categories and tags
  tags?: string[];
  
  // Review dates
  next_review_date?: string;
  last_reviewed_date?: string;
  
  // Metadata
  created_by?: string;
  created_by_email?: string;
  created_at: string;
  updated_at: string;
  
  // Related counts
  tasks_count?: number;
  comments_count?: number;
  attachments_count?: number;
}

export interface RiskComment {
  id: string;
  risk: string;
  comment: string;
  author: string;
  authorEmail: string;
  authorName: string;
  createdAt: string;
}

export interface RiskAttachment {
  id: string;
  risk: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadedBy: string;
  uploadedByEmail: string;
  uploadedByName: string;
  uploadedAt: string;
}

export interface CreateRiskData {
  title: string;
  description: string;
  category: string;
  status?: RiskStatus;
  
  // Organization (exactly one must be set)
  company?: string;
  team?: string;
  department?: string;
  
  // Risk ownership
  owner: string;

  // Free-text vendor/supplier note (vendor management lives elsewhere)
  vendor_note?: string;

  // Inherent risk (required)
  inherent_likelihood: number;  // 1-5
  inherent_impact: number;      // 1-5
  inherent_notes?: string;
  
  // Treatment
  treatment_plan: TreatmentPlan;
  treatment_details?: string;
  treatment_owner?: string;
  treatment_due_date?: string;
  
  // Residual risk (required)
  residual_likelihood: number;  // 1-5
  residual_impact: number;      // 1-5
  residual_notes?: string;
  
  // Other fields
  tags?: string[];
  next_review_date?: string;
  last_reviewed_date?: string;
}

export interface UpdateRiskData extends Partial<CreateRiskData> {
  is_complete?: boolean;
}

export interface CreateRiskCommentData {
  comment: string;
}

export interface PaginatedRisks {
  count: number;
  next: string | null;
  previous: string | null;
  results: Risk[];
}

export type RegisterApiParams = Record<string, string | number | boolean | undefined>;

/** Query-string filter params for the risk list/stats/export endpoints (not the removed RiskFilters UI component). */
export interface RiskQueryFilters {
  status?: RiskStatus;
  category?: RiskCategory;
  likelihood?: RiskLikelihood;
  impact?: RiskImpact;
  owner?: string;
  company?: string;
  team?: string;
  department?: string;
  search?: string;
}

export interface RiskStats {
  totalRisks: number;
  openRisks: number;
  closedRisks: number;
  averageRiskScore: number;
  risksByStatus: Record<RiskStatus, number>;
  risksByCategory: Record<RiskCategory, number>;
  risksByLikelihood: Record<RiskLikelihood, number>;
  risksByImpact: Record<RiskImpact, number>;
  highPriorityRisks: number;
  overdueRisks: number;
}

// Helper functions for risk calculations
export const getLikelihoodValue = (likelihood: RiskLikelihood): number => {
  const values: Record<RiskLikelihood, number> = {
    VERY_LOW: 1,
    LOW: 2,
    MEDIUM: 3,
    HIGH: 4,
    VERY_HIGH: 5,
  };
  return values[likelihood];
};

export const getImpactValue = (impact: RiskImpact): number => {
  const values: Record<RiskImpact, number> = {
    VERY_LOW: 1,
    LOW: 2,
    MEDIUM: 3,
    HIGH: 4,
    VERY_HIGH: 5,
  };
  return values[impact];
};

// Convert number (1-5) to enum string
export const numberToLikelihood = (value: number): RiskLikelihood => {
  const mapping: Record<number, RiskLikelihood> = {
    1: 'VERY_LOW',
    2: 'LOW',
    3: 'MEDIUM',
    4: 'HIGH',
    5: 'VERY_HIGH',
  };
  return mapping[value] || 'MEDIUM';
};

export const numberToImpact = (value: number): RiskImpact => {
  const mapping: Record<number, RiskImpact> = {
    1: 'VERY_LOW',
    2: 'LOW',
    3: 'MEDIUM',
    4: 'HIGH',
    5: 'VERY_HIGH',
  };
  return mapping[value] || 'MEDIUM';
};

export const calculateRiskScore = (likelihood: RiskLikelihood | number, impact: RiskImpact | number): number => {
  const likelihoodValue = typeof likelihood === 'number' ? likelihood : getLikelihoodValue(likelihood);
  const impactValue = typeof impact === 'number' ? impact : getImpactValue(impact);
  return likelihoodValue * impactValue;
};

export const getRiskLevel = (score: number): 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' => {
  // Note: This is a synchronous fallback. Use calculateRiskLevel from utils/settings.utils.ts for dynamic thresholds
  if (score <= 3) return 'LOW';
  if (score <= 9) return 'MEDIUM';
  if (score <= 15) return 'HIGH';
  return 'CRITICAL';
};
